import { Container, Divider } from "@material-ui/core";
import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import DollarIcon from "assets/image-public/images/icon_images/ri_money-dollar-circle-fill.svg";
import axios from "axios";
import {
  CurrencyDisplay,
  CustomTable,
  ModalError,
  ModalSuccess,
  ModalWarning,
  Page,
  PrimaryButton,
  SectionLabel,
  SkeletonComponent,
  StatusChip,
  SwitchInput,
} from "components";
import { InnoImage } from "inno-ui";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { formatDate, getErrors } from "utils";
import {
  chipInvoiceStatus,
  columnTableInvoiceSongDetail,
} from "./Components/ConstantInvoice";

const DetailSalesInvoice = ({ userLogin }) => {
  const classes = globalStyles();
  const history = useHistory();
  const { id } = useParams();
  const inputRef = useRef(null);
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [detail, setDetail] = useState({});
  const [detailPdf, setDetailPdf] = useState({});
  const isFinance = useSelector(
    ({ userState }) => userState?.role?.roles_for === "finance"
  );
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingPrint, setLoadingPrint] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);

  const userPublisher = userLogin?.publisher;
  const publisherLogo =
    userPublisher?.profile_image ||
    "https://sandbox.minio.asaba.co.id/staging-mpis/publisher/ebf4813a-c07f-4ef2-a055-4d9b8597a870.png";
  const approvalStatus = detail?.status;
  const isAllowedToOpenDetailPayment =
    approvalStatus === "paid" ||
    approvalStatus === "waiting_for_approval_payment";
  const showApproved = approvalStatus === "waiting_for_approval" && isFinance;
  const selectedStatus = chipInvoiceStatus?.find(
    ({ value }) => value === approvalStatus
  );
  const hideColumnRemainingRevenue =
    isAllowedToOpenDetailPayment || approvalStatus === "approved";

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const handlePrintPDF = async () => {
    try {
      setLoadingPrint(true);
      await new Promise(resolve => setTimeout(resolve, 0));
      const doc = new jsPDF("p", "mm", "a4");

      const defaultAddress =
        "Jl. Cideng Barat No.54, RT.12/RW.1, Cideng, Kecamatan Gambir, Kota Jakarta Pusat,\nDaerah Khusus Ibukota Jakarta 10000";

      const fontSmall = 7;
      const fontNormal = 9;
      const ptToMm = pt => pt * 0.352777778;
      const lineHeightFactor = 1.15;
      const textSpacing = 8; // Spasi antar baris teks (misalnya, Fee, Tax, Total Fee)
      const lineSpacing = 4; // Spasi untuk garis pemisah

      doc.setFontSize(fontSmall);
      const addressText = userPublisher?.address || defaultAddress;
      const wrappedAddress = doc.splitTextToSize(addressText, 100);
      const addressLineHeight = ptToMm(fontSmall) * lineHeightFactor;
      const lastYAddress = 8 + wrappedAddress.length * addressLineHeight;

      doc.setFontSize(fontNormal);
      const wrappedNote = doc.splitTextToSize(detail?.notes || "", 100);
      const noteLineHeight = ptToMm(fontNormal) * lineHeightFactor;
      const lastYNote = 68 + wrappedNote.length * noteLineHeight;

      const headerBottom = Math.ceil(
        Math.max(lastYAddress + 10, lastYNote + 6, 80)
      );

      // Fungsi header biar bisa dipakai ulang
      const drawHeader = doc => {
        // Logo
        if (userPublisher?.profile_image) {
          try {
            doc.addImage(userPublisher.profile_image, "PNG", 8, 4, 25, 15);
          } catch (err) {
            console.warn("addImage gagal:", err);
          }
        }

        // Header Perusahaan
        doc.setFontSize(fontSmall);
        doc.setTextColor("#6F6F84");
        doc.text(wrappedAddress, 110, 8);

        // Divider
        const lastY = 8 + wrappedAddress.length * addressLineHeight;
        doc.setDrawColor(200);
        doc.line(8, lastY + 10, 200, lastY + 10);

        // Invoice Info
        doc.setTextColor("#263238");
        doc.setFontSize(fontNormal);

        doc.setFont("helvetica", "bold");
        doc.text("Payment To", 8, 40);
        doc.setFont("helvetica", "normal");
        doc.text(detail?.composer_name || "-", 8, 46);

        doc.setFont("helvetica", "bold");
        doc.text("Address", 8, 52);
        doc.setFont("helvetica", "normal");
        const wrappedComposerAddress = doc.splitTextToSize(
          detail?.composer_address || "-",
          100
        );
        doc.text(wrappedComposerAddress, 8, 58);

        doc.setFont("helvetica", "bold");
        doc.text("Invoice Number", 130, 40);
        doc.setFont("helvetica", "normal");
        doc.text(detail?.invoice_number || "-", 130, 46);

        doc.setFont("helvetica", "bold");
        doc.text("Reference", 130, 52);
        doc.setFont("helvetica", "normal");
        doc.text(detail?.reference || "-", 130, 58);

        doc.setFont("helvetica", "bold");
        doc.text("Invoice Date", 130, 64);
        doc.setFont("helvetica", "normal");
        doc.text(formatDate(detail?.invoice_date) || "-", 130, 70);

        doc.setFont("helvetica", "bold");
        doc.text("Note", 8, 64);
        doc.setFont("helvetica", "normal");
        doc.text(wrappedNote, 8, 70);
      };

      let finalY = headerBottom;

      if (showDetail) {
        const data = detailPdf?.map(song => {
          return columnTableInvoiceSongDetail({
            hideColumnRemainingRevenue,
            hideColumnPublisherRevenue: true,
          })?.map(col => {
            switch (col.headerAlign) {
              case "right":
                return `Rp${Number(song?.[col.name] || 0).toLocaleString(
                  "id-ID"
                )}`;

              default:
                return song?.[col.name] || "-";
            }
          });
        });

        autoTable(doc, {
          head: [
            columnTableInvoiceSongDetail({ hideColumnRemainingRevenue, hideColumnPublisherRevenue: true })?.map(
              col => col.title
            ),
          ],
          body: data,
          startY: headerBottom,
          margin: { top: headerBottom },
          styles: { fontSize: 8, cellPadding: 3, valign: "middle" },
          headStyles: {
            fillColor: [230, 230, 230],
            textColor: 20,
            fontStyle: "bold",
          },
          didDrawPage: dataArg => {
            drawHeader(doc, dataArg.pageNumber);
          },
        });

        finalY = doc.lastAutoTable.finalY || headerBottom;
        finalY += 10;
      } else {
        // Kalau tabel disembunyikan, tetap gambar header manual
        drawHeader(doc, 1);
        finalY = headerBottom + 10;
      }

      // =========================================================
      // FEE & TAXES
      // =========================================================

      // 1. Baris Fee
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text("Royalty", 8, finalY);
      doc.text(
        `Rp${Number(detail?.fee).toLocaleString("id-ID")}`,
        200,
        finalY,
        {
          align: "right",
        }
      );

      // Pindah finalY ke bawah baris Fee
      finalY += lineSpacing;
      doc.setDrawColor(200);
      doc.line(8, finalY, 200, finalY);
      finalY += textSpacing - lineSpacing; // Naikkan untuk baris teks berikutnya

      // 2. Baris Taxes (Dinamis)
      if (detail.selected_taxes && detail.selected_taxes.length > 0) {
        detail.selected_taxes.forEach(item => {
          // Tambahkan teks tax
          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);
          doc.text(item.code, 8, finalY);
          // Menggunakan tanda minus (-) karena ini biasanya potongan (Tax Withheld)
          doc.text(
            `${item?.method === "deduction" ? "-" : ""}Rp${Math.abs(
              Number(item?.value)
            ).toLocaleString("id-ID")}`,
            200,
            finalY,
            {
              align: "right",
            }
          );

          // Pindah finalY ke bawah baris Tax
          finalY += lineSpacing;
          doc.setDrawColor(200);
          doc.line(8, finalY, 200, finalY);
          finalY += textSpacing - lineSpacing; // Naikkan untuk baris teks berikutnya
        });
      } else {
        // Jika tidak ada tax, finalY sudah berada pada posisi yang benar setelah Fee
        // Tidak perlu penyesuaian tambahan di sini, kecuali ada kebutuhan spasi ekstra
      }
      if (detail?.cost_allocation_amount !== 0) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);

        // Text Cost Allocation
        doc.text("Cost Allocation", 8, finalY);
        doc.text(
          `-Rp${Number(detail?.cost_allocation_amount).toLocaleString(
            "id-ID"
          )}`,
          200,
          finalY,
          { align: "right" }
        );

        // Turun sedikit untuk garis (sama seperti Fee & Tax)
        finalY += lineSpacing;

        doc.setDrawColor(200);
        doc.line(8, finalY, 200, finalY);

        // Tambah jarak untuk baris berikutnya (Total Royalty)
        finalY += textSpacing - lineSpacing;
      }

      // 3. Baris Total Fee
      doc.setFont("helvetica", "bold");
      doc.text("Total Royalty", 8, finalY); // finalY sudah diposisikan dengan benar
      doc.text(
        `Rp${Number(detail?.total_fee).toLocaleString("id-ID")}`,
        200,
        finalY,
        { align: "right" }
      );

      // =========================================================
      // PENOMORAN HALAMAN AKHIR (Mencegah Bentrokan Footer)
      // =========================================================
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor("#6F6F84");

        // Posisikan nomor halaman 4mm dari bawah (di luar area Total Fee)
        const pageY = doc.internal.pageSize.getHeight() - 4;

        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.getWidth() - 8,
          pageY,
          { align: "right" }
        );
      }

      doc.setPage(pageCount);
      doc.save(`${detail.invoice_number}.pdf`);
    } finally {
      setLoadingPrint(false);
    }
  };
  const handleConfirm = status => {
    const rejected = status === "rejected";
    ModalWarning(
      `Are you sure to ${rejected ? "reject" : "approve"} this data ?`,
      `Confirm ${rejected ? "Reject" : "Approve"} Invoice `
    ).then(res => res?.isConfirmed && handleApproval(status));
  };
  const getDataDetail = async () => {
    try {
      setLoadingPage(true);
      const response = await axios.get(
        `${hardBaseUrl}/publisher/transaction/v2/composer-invoice/${id}`,
        { headers, params: queryParams }
      );
      const { data } = response?.data || {};
      const pageCount = getTotalPage(
        data?.sales_invoices?.total,
        queryParams?.size
      );
      setTableTotalPage(pageCount || 1);
      setDetail(data);
      setDetailPdf(data.sales_invoices?.data?.sales_invoices);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const handleApproval = async type => {
    const approved = type === "approve" ? "approved" : "rejected";
    try {
      setLoadingButton(true);
      await axios.put(
        `${hardBaseUrl}/publisher/transaction/v2/composer-invoice/${
          showApproved ? "" : "payment/"
        }${approved}/${id}`,
        {},
        { headers }
      );
      ModalSuccess(`Succecfully ${type} payment`).then(() => getDataDetail());
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingButton(false);
    }
  };

  useEffect(() => {
    getDataDetail();
  }, [queryParams]);

  return (
    <Page className={classes.root} title="Composer Invoice Detail">
      {loadingPage ? (
        <SkeletonComponent variant="wave" />
      ) : (
        <Container maxWidth={false}>
          <HeaderTitle
            title="Composer Invoice Detail"
            breadcrumbData={breadcrumbData}
            backButton
          />
          <Divider className={classes.divider} />
          <Grid container justifyContent="space-between">
            <Grid item>
              <SectionLabel
                title="Composer Invoice Detail"
                subTitle="This is detail credit note information, in payment transaction"
              />
            </Grid>
            <Grid item display="flex" justifyContent="flex-end">
              <Grid
                container
                direction="column"
                alignItems="flex-end"
                rowSpacing={1}
              >
                <Grid item>
                  <StatusChip
                    label={selectedStatus?.name}
                    type={selectedStatus?.color}
                  />
                </Grid>
                {isAllowedToOpenDetailPayment && (
                  <Grid item>
                    <PrimaryButton
                      startIcon={<img src={DollarIcon} alt="Payment" />}
                      label="PAYMENT"
                      onClick={() =>
                        history.push(
                          `/admin/composer-invoice/detail-payment/${detail?.composer_invoice_id}`
                        )
                      }
                      fontWeight="bold"
                      fontSize={16}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Box
            ref={inputRef}
            border="1px solid #9AA2B1"
            padding="24px"
            borderRadius="8px"
            my="16px"
          >
            <Grid container>
              <Grid item xs={12} md={6}>
                <InnoImage
                  src={publisherLogo}
                  alt="img"
                  fill={true}
                  styleImage={{
                    maxWidth: "155.52px",
                    width: "100%",
                    height: "100%",
                    maxHeight: "50px",
                    objectFit: "contain",
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography textAlign="right" fontSize={14} color="#6F6F84">
                  {userPublisher?.address ||
                    "Jl. Setia Budi, RT 05 RW 05, Karet Kuningan, South Jakarta"}
                </Typography>
              </Grid>
            </Grid>
            <Divider className={classes.divider} />
            <Grid container spacing={2}>
              {mockFieldForm?.map(({ title, key }, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Typography
                    className={classes.fieldTitle}
                    textAlign={index % 2 !== 0 && "right"}
                  >
                    {title}
                  </Typography>
                  <Typography
                    className={classes.fieldValue}
                    textAlign={index % 2 !== 0 && "right"}
                  >
                    {key === "invoice_date"
                      ? formatDate(detail?.[key])
                      : detail?.[key]}
                  </Typography>
                </Grid>
              ))}
              <Grid item xs={12} className="hideWhenPrint">
                <SwitchInput
                  checked={showDetail}
                  onChange={() => setShowDetail(show => !show)}
                  label="Show Source Detail"
                />
              </Grid>
              {showDetail && (
                <Grid item xs={12}>
                  <CustomTable
                    columnTable={columnTableInvoiceSongDetail({
                      hideColumnRemainingRevenue,
                    })}
                    data={detail?.sales_invoices?.data?.sales_invoices}
                    page={queryParams?.page}
                    pageSize={queryParams?.size}
                    pageCount={tableTotalPage}
                    handleChangePage={(_, page) =>
                      handleChangeQueryParams(page, "page")
                    }
                    handleChangePageSize={e =>
                      handleChangeQueryParams(e?.target?.value, "size")
                    }
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Box marginTop="10px" gap={2}>
                  <Card className={classes?.totalWrapper}>
                    <CardContent>
                      <Grid
                        container
                        justifyContent="right"
                        mt="16px"
                        spacing={2}
                      >
                        <Grid item xs={12}>
                          <Box
                            justifyContent="space-between"
                            alignItems="center"
                            display="flex"
                          >
                            <Typography className={classes?.fieldTitle}>
                              Royalty
                            </Typography>
                            <Typography
                              textAlign="right"
                              className={classes?.fieldTitle}
                            >
                              <CurrencyDisplay
                                value={detail?.fee}
                                prefix={"Rp "}
                                decimalScale={2}
                              />
                            </Typography>
                          </Box>
                        </Grid>
                        {(detail?.selected_taxes || [])?.map(tax => (
                          <Grid item xs={12} key={tax?.id}>
                            <Box
                              justifyContent="space-between"
                              alignItems="center"
                              display="flex"
                            >
                              <Typography className={classes?.fieldTitle}>
                                {`${tax?.code}`}
                              </Typography>
                              <Typography
                                textAlign="right"
                                className={classes?.fieldTitle}
                              >
                                <CurrencyDisplay
                                  value={tax?.value}
                                  decimalScale={2}
                                  prefix="Rp"
                                />
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                        {detail?.cost_allocation_amount !== 0 && (
                          <Grid item xs={12}>
                            <Box
                              justifyContent="space-between"
                              alignItems="center"
                              display="flex"
                            >
                              <Typography className={classes?.fieldTitle}>
                                Cost Allocation
                              </Typography>
                              <Typography
                                textAlign="right"
                                className={classes?.fieldTitle}
                              >
                                <CurrencyDisplay
                                  value={-detail?.cost_allocation_amount}
                                  prefix={"Rp "}
                                  decimalScale={2}
                                />
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <Divider className={classes.divider} />
                          <Box
                            justifyContent="space-between"
                            alignItems="center"
                            display="flex"
                          >
                            <Typography>Total Royalty</Typography>
                            <Typography
                              textAlign="right"
                              className={classes?.fieldValue}
                            >
                              <CurrencyDisplay
                                value={detail?.total_fee}
                                prefix={"Rp "}
                                decimalScale={2}
                              />
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Grid container justifyContent="right" gap={1}>
            <PrimaryButton
              onClick={handlePrintPDF}
              disabled={loadingPrint || loadingButton}
              loading={loadingPrint || loadingButton}
              label={
                loadingPrint || loadingButton ? "Generating PDF..." : "Print"
              }
            />
            {showApproved &&
              approvalButtons?.map(({ label, value }, index) => (
                <PrimaryButton
                  key={index}
                  label={loadingButton ? "Processing..." : label}
                  disabled={loadingButton || loadingPrint}
                  loading={loadingButton || loadingPrint}
                  onClick={() => handleConfirm(value)}
                  color={value === "rejected" && "#FF3E00"}
                />
              ))}
          </Grid>
        </Container>
      )}
    </Page>
  );
};

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Transaction",
    link: "/admin/composer-invoice",
  },
  {
    label: "Composer Invoice",
    link: "/admin/composer-invoice",
  },
  {
    label: "Composer Invoice Detail",
    active: true,
  },
];
const mockFieldForm = [
  { title: "Payment To", key: "composer_name" },
  { title: "Invoice Number", key: "invoice_number" },
  { title: "Address", key: "composer_address" },
  { title: "Reference", key: "reference" },
  { title: "Note", key: "notes" },
  { title: "Invoice Date", key: "invoice_date" },
];
const approvalButtons = [
  { label: "Reject", value: "rejected" },
  { label: "Approve", value: "approve" },
];
export default DetailSalesInvoice;
